/**
 * circuit_works.ts — the STADIUM CAMPUS of the Grand Circuit as dressing
 * geometry (phase 5): the start gantry over the line, two grandstand blocks
 * (four tiers each) between the plaza and the straight, the pit lane on the
 * far side with twelve team garages and the paddock slab, the control tower,
 * banner poles, and NPC keep-clears along the track. Procedural, instanced
 * through the dressing builder's Parts/mat4/plank (passed in — no cycle).
 * The loop itself (ribbon, barriers, gantries) is RaceCircuitModule's.
 */
import * as THREE from 'three/webgpu';
import type { SettlementSite } from '../../../shared/settlements.js';
import { campusFrame, CAMPUS_STRAIGHT_OFF_M, type Civilization } from '../../../shared/capitals.js';
import { settlementSites } from '../../../shared/settlements.js';
import { TRACK_HALF_W } from './race_circuit.js';
import type { DressingCollider, DressingSolid, DressingSeat, MatSet } from './settlement_dressing.js';

export interface CircuitWorksInput {
  site: SettlementSite; seed: string; civ: Civilization; M: MatSet; group: THREE.Group;
  parts: { add(kind: string, geo: () => THREE.BufferGeometry, mat: THREE.Material, m4: THREE.Matrix4): void };
  mat4: (x: number, y: number, z: number, yaw: number, sx: number, sy: number, sz: number) => THREE.Matrix4;
  plank: (text: string, civ: Civilization, wM: number, hM: number) => THREE.Mesh;
  gy: (wx: number, wz: number) => number;
  colliders: DressingCollider[]; solids: DressingSolid[]; seats: DressingSeat[];
}

export const STAND_LEN_M = 150;
export const STAND_TIERS = 4;
export const GARAGES = 12;
export const GARAGE_W = 14, GARAGE_D = 12, GARAGE_H = 6, GARAGE_STEP = 48;
export const PIT_LEN_M = 600;
export const TOWER_H = 28;

function yawAlong(dx: number, dz: number): number { return Math.atan2(dx, dz) + Math.PI / 2; }

export function buildCircuitWorks(inp: CircuitWorksInput): { colliders: number; parts: number } {
  const { site, seed, civ, M, group, parts, mat4, plank, gy, colliders, solids, seats } = inp;
  const caps = settlementSites(seed).filter((s) => s.tier === 'capital');
  if (caps.length < 2) return { colliders: 0, parts: 0 };
  const { t, n } = campusFrame(site.centerX, site.centerZ, caps[0].centerX, caps[0].centerZ, caps[1].centerX, caps[1].centerZ);
  const at = (u: number, v: number): [number, number] => [site.centerX + t.x * u + n.x * v, site.centerZ + t.z * u + n.z * v];
  const yawT = yawAlong(t.x, t.z), yawN = yawAlong(n.x, n.z);
  const box = (): THREE.BufferGeometry => new THREE.BoxGeometry(1, 1, 1);
  const cyl = (k: number) => (): THREE.BufferGeometry => new THREE.CylinderGeometry(1, 1, 1, k);
  const clothMat = civ === 'a' ? M.clothA : M.clothB;
  const c0 = colliders.length;
  let count = 0;
  const V = CAMPUS_STRAIGHT_OFF_M; // the straight's centreline
  const y0 = gy(...at(0, V));

  // ── The start gantry over the line ──
  for (const s of [-1, 1]) {
    const [px, pz] = at(0, V + s * (TRACK_HALF_W + 3));
    parts.add('cgpost', box, M.stoneDark, mat4(px, y0 + 6, pz, yawT, 1.2, 12, 1.2)); count++;
    colliders.push({ hx: 0.6, hy: 6, hz: 0.6, x: px, y: y0 + 6, z: pz, yaw: yawT });
  }
  {
    const [bx, bz] = at(0, V);
    parts.add('cgbeam', box, M.stoneDark, mat4(bx, y0 + 12.5, bz, yawN, 2 * (TRACK_HALF_W + 3) + 1.2, 1.4, 1.4)); count++;
    const pl = plank('THE GRAND CIRCUIT · START', civ, 12, 1.4);
    pl.position.set(bx, y0 + 11, bz);
    pl.rotation.y = yawT; // faces along the straight (toward the arriving craft)
    group.add(pl);
    count++;
  }

  // ── Grandstands: two blocks between the plaza and the straight, four tiers rising away from the track ──
  for (const side of [-1, 1]) {
    const u0 = side * (STAND_LEN_M / 2 + 8);
    for (let k = 0; k < STAND_TIERS; k++) {
      const v = V - TRACK_HALF_W - 14 - k * 6;
      const [sx, sz] = at(u0, v);
      const h = 1.6 + k * 1.5;
      parts.add('cstand', box, M.stone, mat4(sx, y0 + h / 2, sz, yawT, STAND_LEN_M, h, 5.5)); count++;
      const pieces = Math.ceil(STAND_LEN_M / 16);
      for (let i = 0; i < pieces; i++) {
        const off = (i + 0.5) * (STAND_LEN_M / pieces) - STAND_LEN_M / 2;
        const [cx, cz] = at(u0 + off, v);
        colliders.push({ hx: STAND_LEN_M / pieces / 2, hy: h / 2, hz: 2.75, x: cx, y: y0 + h / 2, z: cz, yaw: yawT });
      }
    }
    // A roof beam on posts over the top tier.
    const vTop = V - TRACK_HALF_W - 14 - (STAND_TIERS - 1) * 6;
    const [rx, rz] = at(u0, vTop - 2);
    parts.add('cstandroof', box, M.stoneDark, mat4(rx, y0 + 11, rz, yawT, STAND_LEN_M + 4, 0.8, 12)); count++;
    for (const uu of [-STAND_LEN_M / 2, 0, STAND_LEN_M / 2]) {
      const [px, pz] = at(u0 + uu, vTop - 6);
      parts.add('cstandpost', cyl(8), M.stoneDark, mat4(px, y0 + 5.5, pz, 0, 0.5, 11, 0.5)); count++;
      colliders.push({ hx: 0.5, hy: 5.5, hz: 0.5, x: px, y: y0 + 5.5, z: pz, yaw: 0 });
    }
    const [sx, sz] = at(u0, V - TRACK_HALF_W - 14 - 9);
    solids.push({ x: sx, z: sz, hx: STAND_LEN_M / 2, hz: 12, yaw: yawT, base: y0 - 1, top: y0 + 12 });
  }

  // ── Pit lane + garages + paddock on the far side ──
  {
    const vPit = V + TRACK_HALF_W + 8 + 5;
    const [px, pz] = at(0, vPit);
    parts.add('cpitlane', box, M.stoneDark, mat4(px, y0 + 0.05, pz, yawT, PIT_LEN_M, 0.1, 10)); count++;
    const vGar = vPit + 5 + GARAGE_D / 2 + 2;
    for (let g = 0; g < GARAGES; g++) {
      const u = -((GARAGES - 1) * GARAGE_STEP) / 2 + g * GARAGE_STEP;
      const [gx, gz] = at(u, vGar);
      parts.add('cgarage', box, g % 2 === 0 ? M.wallA : M.wallB, mat4(gx, y0 + GARAGE_H / 2, gz, yawT, GARAGE_W, GARAGE_H, GARAGE_D)); count++;
      parts.add('cgarageroof', box, M.stoneDark, mat4(gx, y0 + GARAGE_H + 0.3, gz, yawT, GARAGE_W + 1.5, 0.6, GARAGE_D + 1.5)); count++;
      // The open front: a dark recess plane facing the pit lane.
      const door = new THREE.Mesh(new THREE.PlaneGeometry(GARAGE_W - 2, GARAGE_H - 1), M.stoneDark);
      const [dx, dz] = at(u, vGar - GARAGE_D / 2 - 0.05);
      door.position.set(dx, y0 + (GARAGE_H - 1) / 2, dz);
      door.rotation.y = Math.atan2(-n.x, -n.z);
      group.add(door);
      colliders.push({ hx: GARAGE_W / 2, hy: GARAGE_H / 2, hz: GARAGE_D / 2, x: gx, y: y0 + GARAGE_H / 2, z: gz, yaw: yawT });
      solids.push({ x: gx, z: gz, hx: GARAGE_W / 2, hz: GARAGE_D / 2, yaw: yawT, base: y0 - 1, top: y0 + GARAGE_H });
      const banner = new THREE.Mesh(new THREE.PlaneGeometry(2, 4), clothMat);
      const [bx, bz] = at(u - GARAGE_W / 2 - 1, vGar - GARAGE_D / 2 - 1);
      banner.position.set(bx, y0 + 4, bz);
      banner.rotation.y = Math.atan2(-n.x, -n.z);
      group.add(banner);
    }
    const [qx, qz] = at(0, vGar + GARAGE_D / 2 + 35);
    parts.add('cpaddock', box, M.stone, mat4(qx, y0 + 0.05, qz, yawT, PIT_LEN_M, 0.1, 60)); count++;
    seats.push({ x: px, z: pz, r: 20 });
  }

  // ── The control tower at the far end of the pit lane ──
  {
    const [tx, tz] = at(PIT_LEN_M / 2 + 30, V + TRACK_HALF_W + 30);
    parts.add('ctowerc', box, M.stone, mat4(tx, y0 + TOWER_H / 2, tz, yawT, 10, TOWER_H, 10)); count++;
    parts.add('ctowercap', box, M.stoneDark, mat4(tx, y0 + TOWER_H + 1.5, tz, yawT, 15, 3, 15)); count++;
    colliders.push({ hx: 5, hy: TOWER_H / 2, hz: 5, x: tx, y: y0 + TOWER_H / 2, z: tz, yaw: yawT });
    solids.push({ x: tx, z: tz, hx: 5, hz: 5, yaw: yawT, base: y0 - 1, top: y0 + TOWER_H + 3 });
  }

  // ── Banner poles along the straight's far edge, keep-clears along the track ──
  for (let u = -420; u <= 420; u += 60) {
    const [px, pz] = at(u, V + TRACK_HALF_W + 6);
    parts.add('cpole2', cyl(8), M.woodDark, mat4(px, y0 + 4, pz, 0, 0.14, 8, 0.14)); count++;
    const cloth = new THREE.Mesh(new THREE.PlaneGeometry(1.4, 4), clothMat);
    cloth.position.set(px + t.x * 0.8, y0 + 5.5, pz + t.z * 0.8);
    cloth.rotation.y = Math.atan2(n.x, n.z);
    group.add(cloth);
  }
  for (let u = -450; u <= 450; u += 50) { const [kx, kz] = at(u, V); seats.push({ x: kx, z: kz, r: TRACK_HALF_W + 4 }); }
  return { colliders: colliders.length - c0, parts: count };
}
